import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { MobileFrame } from './components/ui/MobileFrame';
import { BottomTabBar } from './components/ui/BottomTabBar';
import { ThemeProvider } from './contexts/ThemeContext';
import { IncomingCallBanner } from './components/ui/IncomingCallBanner';
import { PresentationScreen } from './pages/PresentationScreen';
import { TransitionScreen } from './pages/TransitionScreen';
// New Onboarding Screens
import { WelcomeScreen } from './pages/WelcomeScreen';
import { FeatureCarouselScreen } from './pages/FeatureCarouselScreen';
import { PhoneEntryScreen } from './pages/PhoneEntryScreen';
import { OTPScreen } from './pages/OTPScreen';
import { NameEntryScreen } from './pages/NameEntryScreen';
import { BackupCheckScreen } from './pages/BackupCheckScreen';
// Main App Screens
import { HomeScreen } from './pages/HomeScreen';
import { CalendarScreen } from './pages/CalendarScreen';
import { CallsScreen } from './pages/CallsScreen';
import { CallDetailScreen } from './pages/CallDetailScreen';
import { ActiveCallScreen } from './pages/ActiveCallScreen';
import { UsageScreen } from './pages/UsageScreen';
import { ProfileScreen } from './pages/ProfileScreen';
import { SettingsScreen } from './pages/SettingsScreen';
import { CallerExperienceScreen } from './pages/CallerExperienceScreen';
import { PlanSelectionScreen } from './pages/PlanSelectionScreen';
import { ActivationGuideScreen } from './pages/ActivationGuideScreen';
import { SupportChatScreen } from './pages/SupportChatScreen';
import { NotificationsScreen } from './pages/NotificationsScreen';
export function App() {
  // Set document title
  useEffect(() => {
    document.title = 'Vexu.ai App Presentation';
  }, []);
  // Top-level view toggle
  const [activeView, setActiveView] = useState<'app' | 'presentation' | 'transition'>('presentation');
  // State for navigation and onboarding
  const [currentScreen, setCurrentScreen] = useState('welcome');
  const [onboardingComplete, setOnboardingComplete] = useState(false);
  const [activeTab, setActiveTab] = useState('home');
  // Store phone number during onboarding
  const [phoneNumber, setPhoneNumber] = useState('');
  const [callDetailOpen, setCallDetailOpen] = useState(false);
  // Activation state (set after plan selection)
  const [isActivated, setIsActivated] = useState(false);
  // Incoming call simulation state
  const [incomingCall, setIncomingCall] = useState<any>(null);
  // Navigation handlers
  const handleGetStarted = () => setCurrentScreen('features');
  const handleExplore = () => {
    setOnboardingComplete(true);
    setCurrentScreen('home');
  };
  const handleFeaturesComplete = () => setCurrentScreen('phoneEntry');
  const handlePhoneSubmit = (phone: string) => {
    setPhoneNumber(phone);
    setCurrentScreen('otp');
  };
  const handleOtpVerify = () => {
    setCurrentScreen('backupCheck');
  };
  const handleNameComplete = (name: string) => {
    setOnboardingComplete(true);
    setCurrentScreen('home');
  };
  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    setCurrentScreen(tab);
  };
  const handleNavigate = (screen: string) => {
    setCurrentScreen(screen);
    if (['home', 'calendar', 'calls', 'usage', 'profile'].includes(screen)) {
      setActiveTab(screen);
    }
  };
  // Mobile detection
  const [isMobile, setIsMobile] = useState(false);
  const [mobileHeaderVisible, setMobileHeaderVisible] = useState(true);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);
  // On mobile, show headers for 4s then hide
  useEffect(() => {
    if (isMobile && activeView === 'presentation') {
      setMobileHeaderVisible(true);
      const timer = setTimeout(() => setMobileHeaderVisible(false), 4000);
      return () => clearTimeout(timer);
    } else {
      setMobileHeaderVisible(true);
    }
  }, [isMobile, activeView]);
  const [showOnboardingWarning, setShowOnboardingWarning] = useState(false);
  const handleSimulateCall = () => {
    if (!onboardingComplete) {
      setShowOnboardingWarning(true);
      setTimeout(() => setShowOnboardingWarning(false), 4000);
      return;
    }
    const callers = [{
      name: 'Alex Johnson',
      initials: 'AJ',
      colorLight: 'bg-blue-100 text-blue-700',
      colorDark: 'bg-blue-100/60 text-blue-600',
      group: 'Work Contacts'
    }, {
      name: 'Emily Chen',
      initials: 'EC',
      colorLight: 'bg-emerald-100 text-emerald-700',
      colorDark: 'bg-emerald-100/60 text-emerald-600',
      group: 'VIP Clients'
    }, {
      name: 'David Park',
      initials: 'DP',
      colorLight: 'bg-purple-100 text-purple-700',
      colorDark: 'bg-purple-100/60 text-purple-600',
      group: 'Personal'
    }];
    const randomCaller = callers[Math.floor(Math.random() * callers.length)];
    setIncomingCall(randomCaller);
    // Auto clear after 6 seconds only if NOT on calls screen (calls screen keeps it persistent)
    if (currentScreen !== 'calls') {
      setTimeout(() => {
        setIncomingCall((prev: any) => prev?.name === randomCaller.name ? null : prev);
      }, 6000);
    }
  };
  // Render current screen content
  const renderScreen = () => {
    switch (currentScreen) {
      // New Onboarding Flow
      case 'welcome':
        return <WelcomeScreen onGetStarted={handleGetStarted} onExplore={handleExplore} data-id="element-0" />;
      case 'features':
        return <FeatureCarouselScreen onContinue={handleFeaturesComplete} data-id="element-1" />;
      case 'phoneEntry':
        return <PhoneEntryScreen onBack={() => setCurrentScreen('features')} onContinue={handlePhoneSubmit} data-id="element-2" />;
      case 'otp':
        return <OTPScreen phoneNumber={phoneNumber} onBack={() => setCurrentScreen('phoneEntry')} onVerify={handleOtpVerify} data-id="element-3" />;
      case 'nameEntry':
        return <NameEntryScreen onBack={() => setCurrentScreen('backupCheck')} onContinue={handleNameComplete} data-id="element-4" />;
      case 'backupCheck':
        return <BackupCheckScreen onBack={() => setCurrentScreen('otp')} onContinue={() => setCurrentScreen('nameEntry')} data-id="element-5" />;
      // Main Tabs
      case 'home':
        return <HomeScreen onNavigate={handleNavigate} isActivated={isActivated} data-id="element-6" />;
      case 'calendar':
        return <CalendarScreen data-id="element-7" />;
      case 'calls':
        return <CallsScreen onNavigate={handleNavigate} onDetailView={setCallDetailOpen} simulatedCall={incomingCall} data-id="element-8" />;
      case 'usage':
        return <UsageScreen onNavigate={handleNavigate} data-id="element-9" />;
      case 'profile':
        return <ProfileScreen onNavigate={handleNavigate} isActivated={isActivated} onLogout={() => {
          setOnboardingComplete(false);
          setIsActivated(false);
          setCurrentScreen('welcome');
        }} data-id="element-10" />;
      // Detail Screens
      case 'planSelection':
        return <PlanSelectionScreen onBack={() => handleNavigate('profile')} onSelectPlan={plan => {
          setIsActivated(true);
          handleNavigate('activationGuide');
        }} data-id="element-11" />;
      case 'activationGuide':
        return <ActivationGuideScreen onComplete={() => handleNavigate('home')} data-id="element-12" />;
      case 'callDetail':
        return <CallDetailScreen onBack={() => handleNavigate('calls')} data-id="element-13" />;
      case 'settings':
        return <SettingsScreen onBack={() => handleNavigate('profile')} data-id="element-14" />;
      case 'support':
        return <SupportChatScreen onBack={() => handleNavigate('profile')} data-id="element-15" />;
      case 'notifications':
        return <NotificationsScreen onBack={() => handleNavigate('profile')} data-id="element-16" />;
      case 'callerExperience':
        return <CallerExperienceScreen onBack={() => handleNavigate('home')} isActivated={isActivated} onNavigate={handleNavigate} data-id="element-17" />;
      case 'activeCall':
        // Hidden screen for demo purposes
        return <ActiveCallScreen onEndCall={() => handleNavigate('home')} data-id="element-18" />;
      default:
        return <HomeScreen onNavigate={handleNavigate} data-id="element-19" />;
    }
  };
  // Check if we should show the bottom tab bar
  const showTabBar = onboardingComplete && ['home', 'calendar', 'calls', 'profile'].includes(currentScreen) && !callDetailOpen;
  return <ThemeProvider data-id="element-20">
      {/* Top-left: Vexu logo */}
      {activeView !== 'transition' && <motion.div initial={isMobile ? {
      opacity: 0,
      y: -20,
      scale: 0.9
    } : false} animate={isMobile ? mobileHeaderVisible ? {
      opacity: 1,
      y: 0,
      scale: 1
    } : {
      opacity: 0,
      y: -30,
      scale: 0.9
    } : {
      opacity: 1,
      y: 0,
      scale: 1
    }} transition={{
      duration: 0.8,
      ease: [0.16, 1, 0.3, 1]
    }} className={`fixed top-4 left-4 z-[100] flex items-center rounded-full backdrop-blur-xl shadow-lg cursor-pointer ${isMobile ? 'gap-2 px-3 py-1.5' : 'gap-3 px-4 py-2.5'} ${activeView === 'presentation' ? 'bg-black border border-zinc-700' : 'bg-white/90 border border-slate-200/60'}`} onClick={() => setActiveView(activeView === 'app' ? 'presentation' : 'app')} style={{
      pointerEvents: isMobile && !mobileHeaderVisible ? 'none' : 'auto'
    }} data-id="element-21">
          <img src="https://cdn.magicpatterns.com/uploads/3A8d459aXLumR8KKFCQtrD/vexu_logo.svg" alt="Vexu" className={`${isMobile ? 'w-5 h-5' : 'w-8 h-8'} object-contain`} data-id="element-22" />
          <span className={`font-bold tracking-tight ${isMobile ? 'text-xs' : 'text-base'} ${activeView === 'presentation' ? 'text-white' : 'text-slate-900'}`} data-id="element-23">
            Vexu.ai
          </span>
        </motion.div>}

      {/* Top-right: Patent Pending */}
      {activeView !== 'transition' && <motion.div initial={isMobile ? {
      opacity: 0,
      y: -20,
      scale: 0.9
    } : false} animate={isMobile ? mobileHeaderVisible ? {
      opacity: 1,
      y: 0,
      scale: [1, 1.03, 1],
      boxShadow: ['0 0 0px rgba(0,200,83,0), inset 0 0 0px rgba(0,200,83,0)', '0 0 20px rgba(0,200,83,0.5), inset 0 0 8px rgba(0,200,83,0.15)', '0 0 4px rgba(0,200,83,0.1), inset 0 0 2px rgba(0,200,83,0.05)', '0 0 20px rgba(0,200,83,0.5), inset 0 0 8px rgba(0,200,83,0.15)', '0 0 0px rgba(0,200,83,0), inset 0 0 0px rgba(0,200,83,0)']
    } : {
      opacity: 0,
      y: -30,
      scale: 0.9
    } : {
      scale: [1, 1.03, 1],
      boxShadow: ['0 0 0px rgba(0,200,83,0), inset 0 0 0px rgba(0,200,83,0)', '0 0 20px rgba(0,200,83,0.5), inset 0 0 8px rgba(0,200,83,0.15)', '0 0 4px rgba(0,200,83,0.1), inset 0 0 2px rgba(0,200,83,0.05)', '0 0 20px rgba(0,200,83,0.5), inset 0 0 8px rgba(0,200,83,0.15)', '0 0 0px rgba(0,200,83,0), inset 0 0 0px rgba(0,200,83,0)'],
      opacity: [0.8, 1, 0.9, 1, 0.8]
    }} transition={isMobile && !mobileHeaderVisible ? {
      duration: 0.8,
      ease: [0.16, 1, 0.3, 1]
    } : {
      duration: 2.5,
      repeat: Infinity,
      ease: 'easeInOut'
    }} className={`fixed top-4 right-4 z-[100] rounded-full backdrop-blur-xl shadow-lg font-bold uppercase tracking-widest text-vexu ${isMobile ? 'px-2.5 py-1.5 text-[9px]' : 'px-4 py-2.5 text-xs'} ${activeView === 'presentation' ? 'bg-black border border-zinc-700' : 'bg-white/90 border border-slate-200/60'}`} style={{
      pointerEvents: isMobile && !mobileHeaderVisible ? 'none' : 'auto'
    }} data-id="element-24">
          Patent Pending
        </motion.div>}

      {activeView === 'presentation' ? <PresentationScreen onEnterApp={() => setActiveView('transition')} data-id="element-25" /> : activeView === 'transition' ? <TransitionScreen onEnterApp={() => setActiveView('app')} data-id="element-26" /> : isMobile /* Mobile: Full screen app, no phone frame */ ? <div className="fixed inset-0 bg-white flex flex-col overflow-hidden" data-id="element-27">
          <AnimatePresence data-id="element-28">
            {incomingCall && ['home', 'calendar', 'profile'].includes(currentScreen) && <IncomingCallBanner key="incoming-banner" call={incomingCall} onTap={() => handleNavigate('calls')} onDismiss={() => setIncomingCall(null)} data-id="element-29" />}
          </AnimatePresence>

          <div className="flex-1 overflow-hidden relative" data-id="element-30">
            {renderScreen()}
          </div>

          {showTabBar && <BottomTabBar currentTab={activeTab} onTabChange={handleTabChange} data-id="element-31" />}
        </div> /* Desktop: Phone frame */ : <MobileFrame onSimulateCall={handleSimulateCall} data-id="element-32">
          <div className="h-full flex flex-col relative" data-id="element-33">
            <AnimatePresence data-id="element-34">
              {incomingCall && ['home', 'calendar', 'profile'].includes(currentScreen) && <IncomingCallBanner key="incoming-banner" call={incomingCall} onTap={() => handleNavigate('calls')} onDismiss={() => setIncomingCall(null)} data-id="element-35" />}
              {showOnboardingWarning && <motion.div key="onboarding-warning" initial={{
            opacity: 0,
            y: -60,
            scale: 0.95
          }} animate={{
            opacity: 1,
            y: 0,
            scale: 1
          }} exit={{
            opacity: 0,
            y: -40,
            scale: 0.95
          }} transition={{
            type: 'spring',
            stiffness: 300,
            damping: 28
          }} className="absolute top-4 left-3 right-3 z-[60]" data-id="element-36">
                  <div className="bg-white rounded-2xl p-4 shadow-xl border border-slate-200 flex items-center gap-3" data-id="element-37">
                    <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0" data-id="element-38">
                      <span className="text-lg" data-id="element-39">👋</span>
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-40">
                      <p className="text-sm font-bold text-slate-900" data-id="element-41">
                        Complete setup first
                      </p>
                      <p className="text-xs text-slate-500 mt-0.5" data-id="element-42">
                        Login or explore the app, then try simulating a call.
                      </p>
                    </div>
                  </div>
                </motion.div>}
            </AnimatePresence>

            <div className="flex-1 overflow-hidden relative" data-id="element-43">
              {renderScreen()}
            </div>

            {showTabBar && <BottomTabBar currentTab={activeTab} onTabChange={handleTabChange} data-id="element-44" />}
          </div>
        </MobileFrame>}

      {/* Contact details — desktop app view only, bottom-right corner */}
      {activeView === 'app' && !isMobile && <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.5,
      duration: 0.6
    }} className="fixed bottom-4 right-4 z-[100]" data-id="element-45">
          <div className="flex flex-col items-end gap-1 px-4 py-3 rounded-2xl backdrop-blur-xl shadow-lg bg-white/90 border border-slate-200/60" data-id="element-46">
            <span className="text-[9px] uppercase tracking-widest font-semibold text-slate-400" data-id="element-47">
              Contact
            </span>
            <a href="mailto:fadi@vexu.ai" className="text-xs font-medium text-slate-600 hover:text-vexu transition-colors" data-id="element-48">
              fadi@vexu.ai
            </a>
            <a href="tel:+971585377090" className="text-xs font-medium text-slate-600 hover:text-vexu transition-colors" data-id="element-49">
              +971 58 537 7090
            </a>
          </div>
        </motion.div>}
    </ThemeProvider>;
}